import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Shield, Phone, Mail, CheckCircle, Clock, Lock, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TradieProfile() {
  // Support both legacy UUID URLs and new SEO-friendly URLs
  const { id, location, service, slug } = useParams();
  const [tradie, setTradie] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  
  // Lead form state
  const [leadForm, setLeadForm] = useState({
    customer_name: '',
    email: '',
    phone: '',
    description: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let tradieUrl;
        if (location && service && slug) {
          // New SEO URL format: /tradies/brisbane/asbestos-removal/ace-asbestos
          tradieUrl = `${API}/tradies/${location}/${service}/${slug}`;
        } else {
          // Legacy format: /tradie/{uuid} or /tradie/{slug}
          tradieUrl = `${API}/tradies/${id}`;
        }
        
        const [tradieRes, servicesRes] = await Promise.all([
          axios.get(tradieUrl),
          axios.get(`${API}/services`)
        ]);
        setTradie(tradieRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error("Error fetching tradie:", error);
        toast.error("Failed to load tradie profile");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, location, service, slug]);

  const getServiceName = (slug) => {
    const service = services.find(s => s.slug === slug);
    return service ? service.name : slug;
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.customer_name || !leadForm.email || !leadForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        customer_name: leadForm.customer_name,
        email: leadForm.email,
        phone: leadForm.phone,
        description: leadForm.description || `Interested in services from ${tradie.business_name}`,
        service_type: tradie.services[0] || '',
        postcode: tradie.postcode,
        suburb: tradie.suburb,
        tradie_id: tradie.id
      });
      
      setLeadSubmitted(true);
      toast.success("Request sent! We'll connect you with this tradie shortly.");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet>
          <title>Loading... | Local Tradies Online</title>
        </Helmet>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#f97316] border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (!tradie) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet>
          <title>Tradie Not Found | Local Tradies Online</title>
        </Helmet>
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-24">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Tradie Not Found</h1>
            <Link to="/browse" className="text-[#f97316] hover:text-[#ea580c] font-bold">
              Back to Browse
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render contact section based on verification status
  const renderContactSection = () => {
    // VERIFIED TRADIES: Show contact details + lead capture form
    if (tradie.verified) {
      return (
        <div className="border-t pt-8" data-testid="contact-section">
          <h2 
            className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Get In Touch
          </h2>
          
          {/* Direct contact buttons for verified tradies */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <a 
              href={`tel:${tradie.phone}`}
              className="flex items-center justify-center space-x-2 bg-[#0f172a] text-white rounded-full px-6 py-4 font-bold hover:bg-[#1e293b] transition-all"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
              <span>{tradie.phone}</span>
            </a>
            
            <a 
              href={`mailto:${tradie.email}`}
              className="flex items-center justify-center space-x-2 bg-[#f97316] text-white rounded-full px-6 py-4 font-bold hover:bg-[#ea580c] transition-all"
              data-testid="email-btn"
            >
              <Mail className="w-5 h-5" />
              <span>Send Email</span>
            </a>
          </div>

          {/* Optional lead capture for verified tradies too */}
          <div className="bg-slate-50 rounded-2xl p-6 mt-6">
            <h3 className="font-bold text-lg text-[#0f172a] mb-4">Or Request a Callback</h3>
            {leadSubmitted ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-semibold">Request sent successfully!</p>
                <p className="text-slate-600 text-sm mt-1">We'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={leadForm.customer_name}
                      onChange={(e) => setLeadForm({...leadForm, customer_name: e.target.value})}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                      placeholder="04XX XXX XXX"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">What do you need help with?</Label>
                  <Textarea
                    id="description"
                    value={leadForm.description}
                    onChange={(e) => setLeadForm({...leadForm, description: e.target.value})}
                    placeholder="Briefly describe your project..."
                    rows={3}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#f97316] hover:bg-[#ea580c]"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Request Callback'}
                </Button>
              </form>
            )}
          </div>
        </div>
      );
    }

    // UNVERIFIED TRADIES: Lead capture form only (no direct contact)
    return (
      <div className="border-t pt-8" data-testid="contact-section">
        <h2 
          className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Contact This Tradie
        </h2>
        
        {/* Lead capture form - required for unverified tradies */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-6">
          {leadSubmitted ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0f172a] mb-2">Request Submitted!</h3>
              <p className="text-slate-600">
                We've received your request and will connect you with {tradie.business_name} shortly.
              </p>
              <p className="text-slate-500 text-sm mt-2">
                A member of our team will be in touch within 24 hours.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-orange-100 rounded-full p-2">
                  <Send className="w-5 h-5 text-[#f97316]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0f172a]">Request a Quote</h3>
                  <p className="text-sm text-slate-600">Fill in your details and we'll connect you</p>
                </div>
              </div>
              
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-[#0f172a]">Your Name *</Label>
                    <Input
                      id="name"
                      value={leadForm.customer_name}
                      onChange={(e) => setLeadForm({...leadForm, customer_name: e.target.value})}
                      placeholder="John Smith"
                      className="bg-white"
                      data-testid="lead-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#0f172a]">Phone *</Label>
                    <Input
                      id="phone"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                      placeholder="04XX XXX XXX"
                      className="bg-white"
                      data-testid="lead-phone-input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#0f172a]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                    placeholder="john@example.com"
                    className="bg-white"
                    data-testid="lead-email-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-[#0f172a]">What do you need help with?</Label>
                  <Textarea
                    id="description"
                    value={leadForm.description}
                    onChange={(e) => setLeadForm({...leadForm, description: e.target.value})}
                    placeholder="Briefly describe your project or the service you need..."
                    className="bg-white"
                    data-testid="lead-description-input"
                    rows={3}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-14 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-lg"
                  disabled={submitting}
                  data-testid="lead-submit-btn"
                >
                  {submitting ? 'Sending Request...' : 'Get Free Quote'}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Your details will be shared with {tradie.business_name} to provide you a quote
                </p>
              </form>
            </>
          )}
        </div>
        
        {/* Info box explaining why no direct contact */}
        <div className="mt-4 bg-slate-100 rounded-xl p-4 flex items-start space-x-3">
          <Lock className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Why request a quote?</span> We verify all contact requests to ensure you receive quality service from trusted tradies.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Build SEO title with safe null checks - using simple string concatenation to avoid Helmet issues
  const tradieTitle = tradie && tradie.business_name 
    ? tradie.business_name + ' | ' + (tradie.services && tradie.services.length > 0 
        ? tradie.services.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ') 
        : 'Services') + ' in ' + (tradie.suburb || 'Australia')
    : 'Tradie Profile';
  
  const tradieDescription = tradie && tradie.business_name
    ? 'Contact ' + tradie.business_name + ' for ' + (tradie.services ? tradie.services.join(', ') : 'trade') + ' services in ' + (tradie.suburb || 'your area') + '. Get a free quote today.'
    : 'Find local tradies in Australia';

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{String(tradieTitle + ' | Local Tradies Online')}</title>
        <meta name="description" content={String(tradieDescription)} />
      </Helmet>
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          {/* Profile Card */}
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden" data-testid="tradie-profile">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-8 md:p-12">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                <div className="flex-1 mb-6 md:mb-0">
                  <div className="flex items-center space-x-3 mb-4">
                    <h1 
                      className="text-3xl md:text-4xl font-bold text-white"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                      data-testid="tradie-business-name"
                    >
                      {tradie.business_name}
                    </h1>
                    {tradie.verified && (
                      <Badge className="bg-green-500 text-white hover:bg-green-500">
                        <Shield className="w-4 h-4 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                  
                  {/* Only show contact name for verified tradies */}
                  {tradie.verified && tradie.contact_name && (
                    <p className="text-lg text-slate-300 mb-4">{tradie.contact_name}</p>
                  )}

                  {tradie.rating > 0 && (
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                        <Star className="w-5 h-5 fill-orange-400 text-orange-400 mr-2" />
                        <span className="font-bold text-white text-lg">{tradie.rating.toFixed(1)}</span>
                        <span className="text-slate-300 ml-2">rating</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12 space-y-8">
              {/* Services */}
              <div data-testid="services-section">
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Services Offered
                </h2>
                <div className="flex flex-wrap gap-2">
                  {tradie.services.map((serviceSlug, index) => (
                    <Link
                      key={index}
                      to={`/browse?service=${serviceSlug}`}
                    >
                      <Badge 
                        className="bg-orange-100 text-orange-700 hover:bg-orange-200 text-sm px-4 py-2 cursor-pointer"
                      >
                        {getServiceName(serviceSlug)}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>

              {/* About */}
              <div data-testid="about-section">
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  About
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  {tradie.description}
                </p>
              </div>

              {/* Location */}
              <div data-testid="location-section">
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Service Area
                </h2>
                <div className="flex items-center space-x-2 text-slate-600">
                  <MapPin className="w-5 h-5 text-[#f97316]" />
                  <span className="text-lg">{tradie.suburb}, {tradie.state} {tradie.postcode}</span>
                </div>
              </div>

              {/* Business Hours */}
              {(tradie.business_hours || tradie.available_24_7) && (
                <div data-testid="business-hours-section">
                  <h2 
                    className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-4"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Availability
                  </h2>
                  <div className="space-y-2">
                    {tradie.available_24_7 && (
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-green-500 text-white hover:bg-green-500">
                          <Clock className="w-3 h-3 mr-1" />
                          Available 24/7
                        </Badge>
                        <span className="text-sm text-slate-600">Emergency callouts available</span>
                      </div>
                    )}
                    {tradie.business_hours && (
                      <div className="flex items-center space-x-2 text-slate-600">
                        <Clock className="w-5 h-5 text-[#f97316]" />
                        <span className="text-base">{tradie.business_hours}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Trust Indicators - Different for verified vs unverified */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="font-bold text-lg text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {tradie.verified ? `Why Choose ${tradie.business_name}?` : 'About This Listing'}
                </h3>
                <div className="space-y-3">
                  {tradie.verified ? (
                    <>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">Verified & Background Checked</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">Fully Insured & Licensed</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">Quality Workmanship Guaranteed</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">Local & Trusted</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3">
                        <Shield className="w-5 h-5 text-amber-500 flex-shrink-0" />
                        <span className="text-slate-700">Awaiting Verification</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        This listing has not yet been verified. Submit your enquiry through our secure form and we'll connect you with this business.
                      </p>
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs text-slate-500">
                          <strong>Are you the owner?</strong> Claim this listing to display your contact details, add photos, and receive enquiries directly.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Contact Section - Different for verified vs unverified */}
              {renderContactSection()}
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-8 text-center">
            <Link 
              to="/browse"
              className="inline-block text-[#f97316] hover:text-[#ea580c] font-bold"
              data-testid="back-to-browse-link"
            >
              Back to Browse
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
